import type { ItemFieldRow } from './item-field-reader';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { scoreNamedIntegerFragmentInteger } from './formula-variable-resolution';
import type { FormulaVariableMetadata, FormulaVariableReaders } from './formula-variable-resolution';
import { statusRowStackInteger } from './status-stack-reader';
import { phpJsonString } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { phpJsonInteger, phpMultiply, phpAdd } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown,key: string): unknown => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,key)
    ? (value as Readonly<Record<string,unknown>>)[key] : undefined;
const trim = (value: string): string => value.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g,'');
function integerArgument(value: unknown): bigint {
    if (value == null || arrayLike(value) || !(typeof value === 'boolean' || isPhpNumeric(value))) throw new TypeError('Integer contribution argument requires an integer');
    if (typeof value === 'string' && /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(value)) {
        const integer = BigInt(value.trim());
        if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return integer;
    }
    const number = Number(value);
    if (!Number.isFinite(number) || number >= 9223372036854775808 || number < -9223372036854775808) throw new TypeError('Integer contribution argument out of range');
    return phpJsonInteger(value);
}
/** Raw PHP string cast + trim, deliberately different from scoring-cell unwrap. */
export function collectIntegerFragmentBraces(fragments: ItemFieldRow): number[] {
    return integerFragmentBraces(fragments).map(Number);
}
export function integerFragmentBraces(fragments: ItemFieldRow): bigint[] {
    if (!arrayLike(fragments)) throw new TypeError('Integer fragments require a PHP array');
    const values: bigint[] = [];
    for (const value of Object.values(fragments)) {
        const match = /^\{(-?\d+)\}$/.exec(trim(phpJsonString(value)));
        if (match) values.push(phpJsonInteger(match[1]));
    }
    return values;
}
export interface IntegerContributionSource {
    category: string; label: string; value: number; href_id: string; kind: string; value_brace?: number;
}
/** Source identity/label rules shared by the two integer contribution producers. */
export function integerContributionSource(category: string,label: string,value: bigint,href: string,kind: string,brace: bigint | null): IntegerContributionSource {
    return {category,label:trim(label)===''?'Unknown source':label,value:Number(value),href_id:href,kind,
        ...(brace === null ? {} : {value_brace:Number(brace)})};
}
export interface StatusIntegerMetadata extends FormulaVariableMetadata { quantityIds: readonly string[] }
export interface StatusIntegerReaders extends FormulaVariableReaders { statuses: (row: ItemFieldRow) => ItemFieldRow }
function scaleInteger(row: ItemFieldRow,value: bigint,metadata: StatusIntegerMetadata,readers: FormulaVariableReaders): bigint {
    if (value === 0n) return 0n;
    const id = phpJsonString(own(row,'status_item_id') ?? readers.itemField(row,'item_id') ?? '');
    if (id === '' || !metadata.quantityIds.includes(id)) return value;
    const stack = statusRowStackInteger(row);
    const scaled = stack > 1n ? phpMultiply(value,stack) : value;
    if (typeof scaled !== 'bigint') throw new TypeError('Status quantity product overflowed its PHP integer return type');
    return scaled;
}
/** ft_status_row_scale_integer: aliases identify the family, never substitute
 * their historic level or cap the instance's stored stack count.
 */
export function scaleStatusInteger(row: ItemFieldRow,value: number,metadata: StatusIntegerMetadata,readers: FormulaVariableReaders): number {
    if (!arrayLike(row)) throw new TypeError('Status quantity scaling requires a PHP array');
    return Number(scaleInteger(row,integerArgument(value),metadata,readers));
}
/** Full ft_status_integer_layer_contribution. Each definition is scored with
 * the actual host as context, quantity-scaled, and retained as ordered provenance.
 */
export function compileStatusIntegerContribution(row: ItemFieldRow,rawLayer: string,metadata: StatusIntegerMetadata,readers: StatusIntegerReaders): {
    total: number; sources: IntegerContributionSource[];
} {
    const result = statusIntegerContributionValue(row,rawLayer,metadata,readers);
    return {...result,total:Number(result.total)};
}
/** Keep the PHP integer/promoted-double distinction until an enclosing rollup casts it. */
export function statusIntegerContributionValue(row: ItemFieldRow,rawLayer: string,metadata: StatusIntegerMetadata,readers: StatusIntegerReaders): {
    total: PhpArithmetic; sources: IntegerContributionSource[];
} {
    if (!arrayLike(row)) throw new TypeError('Status contribution requires a PHP array');
    if (rawLayer == null || arrayLike(rawLayer)) throw new TypeError('Status layer requires a scalar string');
    const layer = phpJsonString(rawLayer), sources: IntegerContributionSource[] = [];
    let total: PhpArithmetic = 0n;
    for (const status of Object.values(readers.statuses(row))) {
        if (!arrayLike(status)) continue;
        const definition = own(status,'status_arr');
        if (!arrayLike(definition) || (readers.itemField(definition,'item_type') ?? '') === 'creature') continue;
        const fragments = readers.layerEntries(definition,layer);
        if (Object.keys(fragments).length === 0) continue;
        let value = scoreNamedIntegerFragmentInteger(row,fragments,layer,metadata,readers);
        const braces = integerFragmentBraces(fragments), brace = braces.length ? braces.reduce((a,b)=>a>b?a:b) : null;
        if (value === 0n && brace === null) continue;
        value = scaleInteger(status,value,metadata,readers); total = phpAdd(total,value);
        const label = phpJsonString(readers.itemField(status,'item_name') ?? readers.itemField(definition,'item_name') ?? 'Status effect');
        const href = phpJsonString(own(status,'status_id') ?? readers.itemField(status,'item_id') ?? '');
        sources.push(integerContributionSource('status_effect',label,value,href,'status_effect',brace));
    }
    return {total,sources};
}
export function createStatusIntegerCompiler(deps: FormulaReadDependencies & {metadata: StatusIntegerMetadata}): {
    contribution: (row: ItemFieldRow,layer: string) => {total: number; sources: IntegerContributionSource[]};
    scale: (row: ItemFieldRow,value: number) => number;
} {
    const context = createFormulaReadContext(deps);
    const readers: StatusIntegerReaders = {...context,statuses:row=>readCreaturePayloadStatuses(row,context.layerValue,id=>{
        deps.trace?.(['reference',id]); const value = deps.resolveCarrierRef(id);
        if (value !== null && !arrayLike(value)) throw new TypeError('Status reference return requires a PHP array or null');
        return value;
    })};
    return {contribution:(row,layer)=>compileStatusIntegerContribution(row,layer,deps.metadata,readers),
        scale:(row,value)=>scaleStatusInteger(row,value,deps.metadata,readers)};
}
